import ApiRequest, { type AxiosError, type AxiosResponse, type ICustomResponse } from '@frontend/request';

import configs from '@aqx-core/common/configs';
import { configViewController } from '@aqx-core/common/services/config';
import storage, { EStorageKey } from '@aqx-core/common/storage';

import { maintenanceViewController } from '../services/maintenance';
import { checkForDownload } from '../utils/export';
import { EHttpResponseCode } from './types';

export * from './types';
export * from './message';

const request = new ApiRequest({
  onRequest: (axiosConfigs) => {
    const url = axiosConfigs.url || '';
    const token = storage.getItem(EStorageKey.TOKEN) as string;

    const axiosBaseConfigs = {
      ...axiosConfigs,
      baseURL: configViewController.getCurrentApiDomain()
    };

    if (token) {
      if (['/api/support/v1/ticket'].includes(url)) {
        axiosBaseConfigs.headers['Authorization'] = `Bearer ${token}`;
        axiosBaseConfigs.headers['Accept'] = 'application/json';
        axiosBaseConfigs.headers['Content-Type'] = 'multipart/form-data';
      } else if (!['/api/auth/v1/company/login'].includes(url)) {
        axiosBaseConfigs.headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return axiosBaseConfigs;
  },
  onResponse: (response: AxiosResponse) => {
    maintenanceViewController.checkForMaintenance(response);
    // Automatically check for file downloads in all responses
    checkForDownload(response);
  },
  // eslint-disable-next-line complexity
  onError: (error: AxiosError<ICustomResponse<never>>) => {
    if (error.response?.status === EHttpResponseCode.METHOD_NOT_ALLOWED) {
      maintenanceViewController.activateMaintenanceMode();
      return null;
    }
    const { config } = error;

    // Check if it's a network or DNS failure, switch to fallback URL if primary URL fails
    if (!configs.apiDomainFallback) {
      // eslint-disable-next-line no-console
      console.debug('⚠️ No fallback URL configured');
      return null;
    }

    // Possible error when the website is blocked by GFW
    const gfwCodes = ['ECONNRESET', 'ETIMEDOUT', 'ECONNABORTED', 'ENOTFOUND', 'ECONNREFUSED'];

    const isNetworkError =
      gfwCodes.includes(error.code || '') ||
      error.message.includes('Network Error') ||
      error.message.includes('timeout') ||
      error.response?.status === EHttpResponseCode.NOT_FOUND ||
      error.response?.status === EHttpResponseCode.BAD_GATEWAY;

    if (!isNetworkError) {
      // eslint-disable-next-line no-console
      console.debug('⚠️ Not a network error, error code:', error.code, 'status:', error.response?.status);
      return null;
    }

    configViewController.setApiResponseError(error.code || 'UNKNOWN_ERROR');
    configViewController.switchToFallbackDomain();

    if (config) {
      config.baseURL = configViewController.getCurrentApiDomain();
      // eslint-disable-next-line no-console
      console.debug('🔄 Switching to fallback URL:', configs.apiDomainFallback);

      // Return the retry promise
      return new ApiRequest({}).get(config.url || '', config);
    }

    return null;
  }
});

export { request };
